'use client';

import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface TonightSpecialSlide {
  imageSrc: string;
  dishName: string;
  theme: string;
  dateMood: string;
  /** Offer details from admin (description) */
  description?: string;
}

interface TonightsSpecialSliderProps {
  className?: string;
  autoPlayIntervalMs?: number;
}

const FALLBACK_SLIDES: TonightSpecialSlide[] = [
  {
    imageSrc: '/assets/img1.png',
    dishName: "Chef's Signature Tandoori Platter",
    theme: 'Spice & Smoke Evening',
    dateMood: 'Tonight',
    description: 'Fresh offers & seasonal picks. Check back for new specials.',
  },
  {
    imageSrc: '/assets/img1.png',
    dishName: 'Herb-Infused Grilled Paneer Feast',
    theme: 'Comfort Classics',
    dateMood: 'Weekend Special',
    description: 'Fresh offers & seasonal picks. Check back for new specials.',
  },
  {
    imageSrc: '/assets/img1.png',
    dishName: 'Royal Nawabi Dinner Spread',
    theme: 'Celebration Night',
    dateMood: 'Friday Mood',
    description: 'Fresh offers & seasonal picks. Check back for new specials.',
  },
];

const TonightsSpecialSlider = ({
  className = '',
  autoPlayIntervalMs = 8000,
}: TonightsSpecialSliderProps) => {
  const [slides, setSlides] = useState<TonightSpecialSlide[]>(FALLBACK_SLIDES);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function fetchOffers() {
      try {
        const res = await fetch('/api/offers');
        if (!res.ok || cancelled) return;
        const data = await res.json();
        if (Array.isArray(data) && data.length > 0) {
          setSlides(
            data.map(
              (o: {
                imageUrl: string;
                dishName: string;
                theme: string;
                dateMood: string;
                description?: string;
              }) => ({
                imageSrc: o.imageUrl,
                dishName: o.dishName,
                theme: o.theme,
                dateMood: o.dateMood,
                description: o.description ?? '',
              })
            )
          );
        }
      } catch (_) {
        // keep fallback slides
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }
    fetchOffers();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (slides.length <= 1) return;
    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % slides.length);
    }, autoPlayIntervalMs);
    return () => clearInterval(interval);
  }, [slides.length, autoPlayIntervalMs]);

  const activeSlide = slides[activeIndex];

  const goTo = (index: number) => {
    setActiveIndex((index + slides.length) % slides.length);
  };

  if (isLoading) {
    return (
      <div className={`mx-auto w-full max-w-3xl ${className}`}>
        <div className="flex aspect-[16/11] w-full overflow-hidden rounded-lg shadow-xl animate-pulse border-2 border-amber-200/50 bg-gradient-to-r from-amber-100/80 via-yellow-100/70 to-orange-100/80">
          <div className="w-[45%] bg-gradient-to-br from-yellow-100 to-amber-100" />
          <div className="w-[10%] min-w-[8px] bg-amber-700/40" />
          <div className="flex-1 bg-amber-50/70" />
        </div>
        <div className="mt-4 flex items-center justify-between rounded-2xl border border-border bg-card/95 px-5 py-4">
          <div className="h-4 w-32 rounded bg-muted" />
          <div className="h-6 w-24 rounded bg-muted" />
        </div>
      </div>
    );
  }

  return (
    <Link
      href="/menu-catalog?currentOffer=true"
      className={`group relative mx-auto block w-full max-w-3xl transition-all duration-500 ease-out hover:scale-[1.02] hover:shadow-2xl focus-ring ${className}`}
      aria-label="View current offers on menu"
    >
      {/* Open book frame: orange-yellow mixed menu book; dynamic offer images rotate inside right page */}
      <div className="flex aspect-[16/11] w-full overflow-hidden rounded-xl shadow-xl border-2 border-amber-300/60 bg-gradient-to-r from-amber-100/80 via-yellow-100/70 to-orange-100/80 transition-shadow duration-300 group-hover:shadow-2xl">
        {/* Left page – Tonight's Special + Offer details from admin */}
        <div className="flex w-[42%] min-w-0 flex-col justify-center border-r-2 border-amber-700/30 bg-gradient-to-br from-yellow-100 via-amber-100 to-orange-100 px-4 py-6 sm:px-5 sm:py-8">
          <h2 className="font-heading text-lg font-semibold tracking-tight text-amber-900 sm:text-xl">
            Tonight&apos;s Special
          </h2>
          <p className="mt-1 text-xs text-amber-800/80 sm:text-sm">
            {activeSlide.theme} • {activeSlide.dateMood}
          </p>
          <div className="mt-3 max-h-24 overflow-y-auto sm:max-h-28">
            <p className="text-xs leading-relaxed text-amber-900/80 sm:text-sm">
              {activeSlide.description?.trim()
                ? activeSlide.description
                : 'Fresh offers & seasonal picks. Check back for new specials.'}
            </p>
          </div>
        </div>
        {/* Spine */}
        <div className="w-[6%] min-w-[6px] max-w-[12px] bg-gradient-to-b from-amber-700/50 via-amber-800/60 to-amber-700/50 shadow-inner" aria-hidden />
        {/* Right page – uploaded offer images rotate here (same API, dynamic) */}
        <div className="relative flex-1 min-w-0 overflow-hidden bg-gradient-to-bl from-amber-50 to-yellow-50/90">
          {slides.map((slide, index) => (
            <div
              key={slide.dishName + index}
              className={`absolute inset-0 transition-opacity duration-500 ease-out ${
                index === activeIndex ? 'opacity-100' : 'opacity-0 pointer-events-none'
              }`}
            >
              <AppImage
                src={slide.imageSrc}
                alt={`${slide.dishName} – ${slide.theme}`}
                className="h-full w-full object-cover"
                fill
                priority={index === 0}
              />
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
            </div>
          ))}

          {slides.length > 1 && (
            <>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  goTo(activeIndex - 1);
                }}
                className="absolute left-1 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-black/50 text-white shadow-lg backdrop-blur transition-smooth hover:bg-black/65 sm:left-2 sm:h-9 sm:w-9"
                aria-label="Previous special"
              >
                <Icon name="ChevronLeftIcon" size={16} />
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  goTo(activeIndex + 1);
                }}
                className="absolute right-1 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-black/50 text-white shadow-lg backdrop-blur transition-smooth hover:bg-black/65 sm:right-2 sm:h-9 sm:w-9"
                aria-label="Next special"
              >
                <Icon name="ChevronRightIcon" size={16} />
              </button>
            </>
          )}

          {slides.length > 1 && (
            <div className="pointer-events-none absolute bottom-2 left-1/2 flex -translate-x-1/2 space-x-1.5 sm:bottom-3">
              {slides.map((_, index) => (
                <span
                  key={index}
                  className={`block h-1.5 w-1.5 rounded-full sm:h-2 sm:w-2 ${
                    index === activeIndex ? 'bg-white shadow-md' : 'bg-white/50'
                  }`}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Info bar below book */}
      <div className="mt-4 flex items-center justify-between rounded-2xl border border-border bg-card/95 px-5 py-4 shadow-md backdrop-blur transition-smooth group-hover:shadow-lg">
        <div>
          <p className="text-sm font-semibold text-foreground">
            What&apos;s Special: {activeSlide.dishName}
          </p>
          <p className="text-xs text-muted-foreground">
            {activeSlide.theme} • {activeSlide.dateMood}
          </p>
        </div>
        <div className="inline-flex items-center space-x-1 rounded-full bg-muted px-3 py-1 text-xs font-medium text-muted-foreground">
          <Icon name="BuildingStorefrontIcon" size={14} className="text-accent" />
          <span>Special Offer</span>
        </div>
      </div>
    </Link>
  );
};

export default TonightsSpecialSlider;
